import { useEffect, useState } from 'react';
import { Lightbulb, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type RecommendationPriority = 'high' | 'medium' | 'low';

export interface AgentRecommendationData {
  id: string;
  message: string;
  priority: RecommendationPriority;
  shownAt: number;
}

export interface AgentRecommendationChipProps {
  recommendation: AgentRecommendationData;
  /** Auto-dismiss timeout in ms — defaults to 60s per §2.4 */
  autoDismissMs?: number;
  onAccept: (rec: AgentRecommendationData) => void;
  onDismiss: (rec: AgentRecommendationData) => void;
}

const PRIORITY_STYLES: Record<RecommendationPriority, string> = {
  // Per §2.4: high=ámbar, medium=azul, low=gris claro
  high: 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100',
  medium: 'border-primary/40 bg-primary/10 text-foreground',
  low: 'border-muted-foreground/20 bg-muted/40 text-muted-foreground',
};

export function AgentRecommendationChip({
  recommendation,
  autoDismissMs = 60_000,
  onAccept,
  onDismiss,
}: AgentRecommendationChipProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      onDismiss(recommendation);
    }, autoDismissMs);
    return () => clearTimeout(t);
  }, [recommendation, autoDismissMs, onDismiss]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-rec-id={recommendation.id}
      data-rec-priority={recommendation.priority}
      className={cn(
        'mt-1.5 rounded-md border px-2.5 py-2 text-xs flex items-start gap-2',
        PRIORITY_STYLES[recommendation.priority],
      )}
    >
      <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-80" />
      <div className="flex-1 min-w-0">
        <div className="leading-snug whitespace-normal break-words">
          {recommendation.message}
        </div>
        <div className="mt-1.5 flex gap-1.5">
          <Button
            size="sm"
            variant="default"
            className="h-6 px-2 text-[11px] gap-1"
            onClick={() => {
              setVisible(false);
              onAccept(recommendation);
            }}
            data-testid={`rec-accept-${recommendation.id}`}
          >
            <Check className="h-3 w-3" /> Sí
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[11px] gap-1"
            onClick={() => {
              setVisible(false);
              onDismiss(recommendation);
            }}
            data-testid={`rec-dismiss-${recommendation.id}`}
          >
            <X className="h-3 w-3" /> No
          </Button>
        </div>
      </div>
    </div>
  );
}
